import { Alert, Button, Container, Dropdown, Modal } from "react-bootstrap";
import classNames from "classnames";
import "./Booking.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faEdit,
  faExclamationTriangle,
  faFileExcel,
  faFileExport,
  faFilePdf,
  faInfoCircle,
  faList,
  faPalette,
  faSave,
  faTrash,
  faWind,
} from "@fortawesome/free-solid-svg-icons";
import {
  BOOKING_CUSTOM_TYPE_COLOR,
  BOOKING_TYPE_COLORS,
} from "../../utils/bookingColor";
import { rememberCustomType } from "../../utils/customBookingType";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CompleteBooking,
  CreateBookingDTO,
  UpdateBookingDTO,
} from "../../types/Booking";
import { TailSpin } from "react-loader-spinner";
import { Bookings } from "../../api/Booking";
import { useToasts } from "../../contexts/toasts";
import { Option } from "../../models/generics";
import { ConfigProvider, DatePicker, Select } from "antd";
import { getSportName, isPastDateTime } from "../../utils/stringUtil";
import { meterPerSecondToKmPerHour } from "../../utils/numberUtil";
import { Facility, TimeSlot } from "../../types/Structure";
import { Calendar } from "../../Components/Calendar/Calendar";
import { Weather } from "../../api/Weather";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/it";
import locale from "antd/locale/it_IT";
import localeData from "dayjs/plugin/localeData";
import IconButton from "../../common/IconButton";
import BookingModal from "./BookingModal";
import NewBookingModal from "./NewBookingModal";
import { useAuth } from "../../contexts/auth";
import {
  BookingMode,
  BookingScope,
  BookingStatus,
  BookingType,
} from "../../enums/BookingScope";
import {
  CreateMatchResultDTO,
  MatchType,
  PadelScore,
  UpdateMatchDTO,
  WinnerTeam,
} from "../../types/Result";
import { Matches } from "../../api/Match";
import UpdateResult from "./UpdateResult";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { pdf } from "@react-pdf/renderer";
import moment from "moment";
import { buildBookingsExport } from "./export/bookingsExportData";
import BookingsReportPdf from "./export/BookingsReportPdf";
import {
  closePreviewWindow,
  openPreviewWindow,
  showExcelPreview,
  showPdfPreview,
} from "./export/bookingsExportPreview";

import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import FreeCalendar from "../../Components/Calendar/FreeCalendar";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

dayjs.extend(localeData);
dayjs.locale("it");

// Le calendar vengono memoizzate: si ridisegnano solo quando cambiano i
// propri dati, non a ogni digitazione nella modale della pagina.
const MemoCalendar = React.memo(Calendar);
const MemoFreeCalendar = React.memo(FreeCalendar);

// Legenda dei colori dei blocchi (tipo prenotazione + colore personalizzato).
const LEGEND_ITEMS: { label: string; color: string }[] = [
  { label: "Amichevole", color: BOOKING_TYPE_COLORS[BookingType.Friendly] },
  { label: "Torneo", color: BOOKING_TYPE_COLORS[BookingType.Tournament] },
  {
    label: "Lezione / Allenamento",
    color: BOOKING_TYPE_COLORS[BookingType.Training],
  },
  { label: "Tipologia personalizzata", color: BOOKING_CUSTOM_TYPE_COLOR },
];

function BookingsLegend() {
  return (
    <div className="bk-legend" aria-label="Legenda colori">
      {LEGEND_ITEMS.map((item) => (
        <span key={item.label} className="bk-legend-item">
          <span className="bk-color-dot" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
      <span className="bk-legend-item">
        <span className="bk-color-dot bk-color-dot--custom">
          <FontAwesomeIcon icon={faPalette} />
        </span>
        Colore scelto dalla struttura
      </span>
    </div>
  );
}

type ExportFormat = "pdf" | "excel";

export default function BookingsList() {
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState<Option[]>();
  const [selectedSport, setSelectedSport] = useState<number | undefined>();
  const [facilitiesWithBookings, setFacilitiesWithBookings] =
    useState<Facility[]>();
  const [weatherData, setWeatherData] = useState<any>();
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const [weather, setWeather] = useState<any>();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<CompleteBooking>();
  const [selectedTime, setSelectedTime] = useState<TimeSlot>();
  const [selectedFacility, setSelectedFacility] = useState<Facility>();
  const [modalMode, setModalMode] = useState<"view" | "edit">("view");
  const [saveLoading, setSaveLoading] = useState(false);
  const [bookingData, setBookingData] = useState<{
    matchType?: MatchType;
    bookingType?: BookingType;
    scope: BookingScope;
    inviteLevel?: number;
    coach?: string;
    player1Team1?: string;
    player2Team1?: string;
    player1Team2?: string;
    player2Team2?: string;
    notes?: string;
    color?: string;
    // undefined = tipo standard; stringa (anche vuota) = tipologia personalizzata.
    customType?: string;
  }>({
    matchType: MatchType.Double,
    scope: BookingScope.Private,
    bookingType: undefined,
    inviteLevel: undefined,
    coach: undefined,
    player1Team1: undefined,
    player2Team1: undefined,
    player1Team2: undefined,
    player2Team2: undefined,
    notes: undefined,
    color: undefined,
    customType: undefined,
  });
  const [selectedMode, setSelectedMode] = useState<BookingMode>();
  const [updateResultModal, setUpdateResultModal] = useState(false);
  const [freeModeSlots, setFreeModeSlots] = useState<string[]>([]);
  const initialBookingDataRef = useRef(bookingData);

  const initSports = useCallback(() => {
    Bookings.getUserSports().then(
      ({ data }) => {
        setSports(
          data.list.map((sport) => ({
            label: getSportName(sport.name),
            value: sport.id,
          }))
        );
        if (data.list.length === 1) {
          setSelectedSport(data.list[0].id);
        }
      },
      () => {
        showToast(
          "sports-error",
          "Impossibile ottenere gli sport",
          "",
          "danger"
        );
        setSports([]);
        setError(true);
      }
    );
  }, [showToast]);

  const initWeather = useCallback(() => {
    Weather.getWeatherInfos().then(
      ({ data }) => {
        setWeatherData(data);
      },
      (err) => {
        setWeatherData(undefined);
      }
    );
  }, []);

  const initBookings = useCallback(() => {
    setError(false);
    setFacilitiesWithBookings(undefined);
    if (selectedDate) {
      const formattedDate = selectedDate.format("YYYY-MM-DD");
      Bookings.getAdminBookings(selectedSport!, formattedDate).then(
        ({ data }) => {
          setFacilitiesWithBookings(data.list);
        },
        () => {
          setFacilitiesWithBookings([]);
          setError(true);
          showToast(
            "bookings-retriev-error",
            "Errore nella lista delle Prenotazioni, per favore riprova.",
            "",
            "danger"
          );
        }
      );
    }
  }, [showToast, selectedSport, selectedDate]);

  const generateSlots = useCallback(() => {
    const slots: string[] = [];
    let current = moment("08:00", "HH:mm");
    const endTime = moment("23:30", "HH:mm");

    while (current <= endTime) {
      slots.push(current.format("HH:mm"));
      current.add(30, "minutes");
    }

    setFreeModeSlots(slots);
  }, []);

  useEffect(() => {
    if (selectedMode === BookingMode.Free) generateSlots();
  }, [selectedMode, generateSlots]);

  const initStructureVisualization = useCallback(() => {
    if (loggedUser)
      setSelectedMode(
        loggedUser!.structure![0].preference || BookingMode.Slots
      );
  }, [loggedUser]);

  useEffect(() => {
    initSports();
    initWeather();
    initStructureVisualization();
  }, [initSports, initWeather, initStructureVisualization]);

  const reset = useCallback(() => {
    setError(false);
    setFacilitiesWithBookings(undefined);
    initBookings();
  }, [initBookings]);

  const onDeleteBooking = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.stopPropagation();
    setShowBookingModal(false);
    setShowDeleteModal(true);
  };

  const deleteBooking = () => {
    setLoading(true);

    Bookings.deleteBooking(selectedBooking!.id).then(
      () => {
        showToast(
          "booking-delete-success",
          "Prenotazione cancellata correttamente",
          "",
          "success"
        );
        setLoading(false);
        reset();
        handleDeleteClose();
      },
      () => {
        setLoading(false);
        showToast(
          "booking-delete-error",
          "Errore nella cancellazione della prenotazione",
          "",
          "danger"
        );
      }
    );
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedBooking(undefined);
    setSelectedTime(undefined);
    setModalMode("view");
    setBookingData(initialBookingDataRef.current);
    setSelectedFacility(undefined);
  };

  useEffect(() => {
    if (selectedSport) {
      setSelectedDate(dayjs());
    }
  }, [selectedSport]);

  // Stabile (useCallback) così le calendar memoizzate non si ridisegnano a
  // ogni render della pagina.
  const openBookingModal = useCallback(
    (time: TimeSlot, facility: Facility, booking?: CompleteBooking) => {
      if (booking) {
        setSelectedBooking(booking);
      }
      setSelectedTime(time);
      setModalMode("view");
      setBookingData(initialBookingDataRef.current);
      setSelectedFacility(facility);
      setShowBookingModal(true);
      if (booking) {
        setBookingData({
          matchType: booking.match?.matchType || MatchType.Double,
          bookingType: booking.bookingType,
          scope: booking.scope,
          inviteLevel: booking?.inviteLevel,
          coach: booking.coachId,
          player1Team1: booking.match?.player1Team1Id,
          player2Team1: booking.match?.player2Team1Id,
          player1Team2: booking.match?.player1Team2Id,
          player2Team2: booking.match?.player2Team2Id,
          notes: booking.notes || "",
          color: booking.color || undefined,
          customType: booking.customType || undefined,
        });
      }
    },
    []
  );

  const handleFieldChange = useCallback(
    (key: string) => (value: string) => {
      if (key === "scope") {
        setBookingData((previous) => ({
          ...previous,
          inviteLevel: undefined,
        }));
      }
      if (key === "matchType") {
        setBookingData((previous) => ({
          ...previous,
          player2Team1: undefined,
          player2Team2: undefined,
        }));
      }
      setBookingData((previous) => ({
        ...previous,
        [key]: value,
      }));
    },
    []
  );

  const handleInviteLevelChange = useCallback((e: number) => {
    setBookingData((previous) => ({
      ...previous,
      inviteLevel: e,
    }));
  }, []);

  const handleFieldClear = useCallback(
    (key: string) => () => {
      setBookingData((previous) => ({
        ...previous,
        [key]: undefined,
      }));
    },
    []
  );

  const renderBookings = useCallback(() => {
    if (!selectedSport) return;
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    if (!facilitiesWithBookings)
      return (
        <div className="bk-state">
          <TailSpin height={64} width={64} color="#f25d0b" radius="1" />
          <span className="bk-state-text">Carico le prenotazioni…</span>
        </div>
      );

    if (facilitiesWithBookings.length === 0)
      return (
        <div className="bk-state">
          <FontAwesomeIcon
            icon={faInfoCircle}
            size="2x"
            className="text-secondary"
          />
          <span className="bk-state-text">
            Nessun campo attivo per questo sport in questa data
          </span>
        </div>
      );

    // I campi "lezione" vanno in coda, dopo quelli normali.
    const isLessonFacility = (facility: Facility) =>
      /lezion?/i.test(facility.name);
    const ordered = [
      ...facilitiesWithBookings.filter((f) => !isLessonFacility(f)),
      ...facilitiesWithBookings.filter(isLessonFacility),
    ];

    return (
      <DndProvider backend={HTML5Backend}>
        <div className="bk-facilities">
          {ordered.map((f) => {
            const dayBookings = (f.bookings || []).filter(
              (b) => b.date === selectedDate!.format("YYYY-MM-DD")
            ).length;
            return (
              <section
                key={f.id!}
                className={classNames("bk-facility-card", {
                  "bk-facility-card--lesson": isLessonFacility(f),
                })}
              >
                <span className="bk-facility-count">
                  {dayBookings === 0
                    ? "Nessuna prenotazione"
                    : dayBookings === 1
                    ? "1 prenotazione"
                    : `${dayBookings} prenotazioni`}
                </span>
                {selectedMode === BookingMode.Free ? (
                  <MemoFreeCalendar
                    slots={freeModeSlots}
                    date={selectedDate!}
                    facility={f}
                    onReset={reset}
                    onItemClick={openBookingModal}
                  />
                ) : (
                  <MemoCalendar
                    onItemClick={openBookingModal}
                    facility={f}
                    date={selectedDate!}
                    onReset={reset}
                  />
                )}
              </section>
            );
          })}
        </div>
      </DndProvider>
    );
  }, [
    facilitiesWithBookings,
    error,
    selectedSport,
    selectedDate,
    reset,
    selectedMode,
    freeModeSlots,
    openBookingModal,
  ]);

  const changeDate = (days: number) => () => {
    setSelectedDate((prev) => prev?.add(days, "day"));
  };

  const goToToday = useCallback(() => setSelectedDate(dayjs()), []);

  const isToday = !!selectedDate && selectedDate.isSame(dayjs(), "day");

  // Esportazione (PDF / Excel): il file viene generato al click e mostrato in
  // anteprima in una nuova scheda, senza download automatico.
  const [exporting, setExporting] = useState<ExportFormat>();
  const canExport =
    !!selectedDate && !!facilitiesWithBookings && facilitiesWithBookings.length > 0;

  const exportBookings = useCallback(
    (format: ExportFormat) => {
      if (!selectedDate || !facilitiesWithBookings || exporting) return;

      // La scheda va aperta subito, nel click, o il popup blocker la ferma.
      const label = format === "pdf" ? "Anteprima PDF" : "Anteprima Excel";
      const win = openPreviewWindow(label);
      if (!win) {
        showToast(
          "export-popup-blocked",
          "Il browser ha bloccato l'apertura della nuova scheda",
          "Consenti i popup per questo sito e riprova.",
          "warning"
        );
        return;
      }

      setExporting(format);
      const data = buildBookingsExport({
        facilities: facilitiesWithBookings,
        date: selectedDate,
        structure: loggedUser?.structure?.[0],
        weather: weather
          ? {
              min: weather.main?.temp_min ?? 0,
              max: weather.main?.temp_max ?? 0,
              windKmh: meterPerSecondToKmPerHour(weather.wind?.speed ?? 0),
              description: weather.weather?.[0]?.description,
            }
          : undefined,
      });

      const run = async () => {
        if (format === "pdf") {
          const blob = await pdf(<BookingsReportPdf data={data} />).toBlob();
          await showPdfPreview(win, blob, `${data.fileBaseName}.pdf`, data);
        } else {
          // exceljs pesa parecchio: lo carichiamo solo quando serve davvero.
          const { buildBookingsWorkbook } = await import("./export/bookingsExcel");
          const blob = await buildBookingsWorkbook(data);
          await showExcelPreview(win, blob, `${data.fileBaseName}.xlsx`, data);
        }
      };

      run()
        .catch((err) => {
          console.error("Errore esportazione prenotazioni", err);
          closePreviewWindow(win);
          showToast(
            "export-error",
            "Impossibile generare il file",
            "Riprova tra qualche istante.",
            "danger"
          );
        })
        .finally(() => setExporting(undefined));
    },
    [selectedDate, facilitiesWithBookings, exporting, loggedUser, weather, showToast]
  );

  useEffect(() => {
    if (selectedDate) initBookings();
  }, [selectedDate, initBookings]);

  useEffect(() => {
    if (weatherData && selectedDate) {
      if (selectedDate.format("YYYY-MM-DD") < dayjs().format("YYYY-MM-DD")) {
        setWeather(undefined);
      } else {
        if (selectedDate.format("YYYY-MM-DD") > dayjs().format("YYYY-MM-DD")) {
          setWeather(
            weatherData.list.filter(
              (w: any) =>
                w.dt_txt.split(" ")[0] === selectedDate.format("YYYY-MM-DD")
            )[4]
          );
        } else {
          setWeather(
            weatherData.list.filter(
              (w: any) =>
                w.dt_txt.split(" ")[0] === selectedDate.format("YYYY-MM-DD")
            )[0]
          );
        }
      }
    }
  }, [weatherData, selectedDate]);

  const handleBookingModalClose = useCallback(() => {
    setShowBookingModal(false);
    setUpdateResultModal(false);
    setSelectedBooking(undefined);
    setSelectedTime(undefined);
    setModalMode("view");
    setBookingData(initialBookingDataRef.current);
    setSelectedFacility(undefined);
  }, []);

  const handleDateChange = useCallback((date: Dayjs) => {
    setSelectedDate(dayjs(date));
  }, []);

  const renderAsyncContent = () => {
    return sports ? (
      <div className="d-flex flex-column gap-3">{renderBookings()}</div>
    ) : (
      <div className="bk-state">
        <TailSpin height={64} width={64} color="#f25d0b" radius="1" />
      </div>
    );
  };

  // Toolbar unica: titolo, sport, navigazione data, stampa, meteo e legenda.
  const renderToolbar = () => {
    return (
      <header className="bk-toolbar">
        <div className="bk-toolbar-row">
          <div className="bk-toolbar-title">
            <h4 className="mb-0">Prenotazioni</h4>
            {selectedDate && (
              <span className="bk-toolbar-subtitle">
                {selectedDate.format("dddd D MMMM YYYY")}
                {isToday && <span className="bk-today-badge">Oggi</span>}
              </span>
            )}
          </div>

          <div className="bk-toolbar-controls">
            {sports && (
              <Select
                className="bk-sport-select"
                placeholder="Scegli lo sport…"
                value={selectedSport}
                onSelect={setSelectedSport}
                allowClear
                options={sports}
                onDeselect={setSelectedSport}
              />
            )}

            {selectedDate && (
              <div className="bk-date-nav">
                <IconButton title="Giorno precedente" onClick={changeDate(-1)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <ConfigProvider locale={locale}>
                  <DatePicker
                    className="bk-date-picker"
                    value={selectedDate}
                    allowClear={false}
                    onChange={handleDateChange}
                    format={(value) => value.format("ddd D MMM YYYY")}
                    placeholder="Seleziona una data"
                  />
                </ConfigProvider>
                <IconButton title="Giorno successivo" onClick={changeDate(1)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <button
                  type="button"
                  className={classNames("bk-today-button", {
                    active: isToday,
                  })}
                  onClick={goToToday}
                  disabled={isToday}
                  title="Vai a oggi"
                >
                  <FontAwesomeIcon icon={faCalendarDay} />
                  <span className="ms-2">Oggi</span>
                </button>
              </div>
            )}

            {selectedDate && (
              <Dropdown align="end" className="bk-export">
                <Dropdown.Toggle
                  size="sm"
                  variant="success"
                  className="bk-export-toggle"
                  disabled={!canExport || !!exporting}
                  title={
                    canExport
                      ? "Esporta le prenotazioni del giorno"
                      : "Nessuna prenotazione da esportare"
                  }
                >
                  <FontAwesomeIcon icon={faFileExport} />
                  <span className="ms-2">
                    {exporting ? "Generazione…" : "Esporta"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="bk-export-menu">
                  <Dropdown.Item onClick={() => exportBookings("pdf")}>
                    <FontAwesomeIcon icon={faFilePdf} className="bk-export-icon bk-export-icon--pdf" />
                    <span>
                      <strong>PDF</strong>
                      <small>Report impaginato, pronto da stampare</small>
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => exportBookings("excel")}>
                    <FontAwesomeIcon icon={faFileExcel} className="bk-export-icon bk-export-icon--xlsx" />
                    <span>
                      <strong>Excel</strong>
                      <small>Foglio di calcolo con riepilogo e filtri</small>
                    </span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>

        <div className="bk-toolbar-row bk-toolbar-row--secondary">
          <BookingsLegend />
          {weather && (
            <div className="bk-weather" title="Previsioni per la data scelta">
              <img
                src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}.png`}
                alt={weather.weather[0].description || "Meteo"}
                width={36}
                height={36}
              />
              <span>
                <strong>{Math.round(weather.main.temp_min)}°</strong> /{" "}
                <strong>{Math.round(weather.main.temp_max)}°</strong>
              </span>
              <span className="bk-weather-wind">
                <FontAwesomeIcon icon={faWind} className="me-1" />
                {meterPerSecondToKmPerHour(weather.wind.speed)} km/h
              </span>
            </div>
          )}
        </div>
      </header>
    );
  };

  const handleCreateBooking = useCallback(() => {
    if (selectedDate && selectedTime && selectedFacility && selectedSport) {
      if (bookingData.bookingType === BookingType.Training) {
        if ([1, 2, 7].includes(selectedSport)) {
          if (
            !bookingData.player1Team1 &&
            !bookingData.player2Team1 &&
            !bookingData.player1Team2 &&
            !bookingData.player2Team2
          ) {
            return;
          }
        }
        const payload: CreateBookingDTO = {
          date: selectedDate.format("YYYY-MM-DDTHH:mm:ss").split("T")[0],
          time: selectedTime.time || "",
          facilityId: selectedFacility.id!,
          structureId: selectedFacility.structureId,
          userId: loggedUser!.id,
          status: BookingStatus.Pending,
          scope: bookingData.scope || BookingScope.Private,
          bookingType: bookingData.bookingType || BookingType.Friendly,
          inviteLevel: bookingData.inviteLevel,
          coachId: bookingData.coach,
          sportId: selectedFacility.sportId,
          participantIds: [],
          createdByHost: true,
          notes: bookingData.notes || "",
          color: bookingData.color || null,
          customType: bookingData.customType?.trim() || null,
        };
        rememberCustomType(payload.customType);
        setSaveLoading(true);
        Bookings.createBooking(payload).then(
          ({ data }) => {
            const matchPayload: CreateMatchResultDTO = {
              sportId: data.booking.sportId,
              bookingId: data.booking.id,
              matchType: bookingData.matchType || MatchType.Double,
              score: [],
              player1Team1Id: bookingData.player1Team1,
              player2Team1Id: bookingData.player2Team1,
              player1Team2Id: bookingData.player1Team2,
              player2Team2Id: bookingData.player2Team2,
            };
            if (data.booking.sportId === 1) {
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-create-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                (err) => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else if (data.booking.sportId === 2) {
              matchPayload.matchType = bookingData.matchType;
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-create-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                () => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else if (data.booking.sportId === 7) {
              matchPayload.matchType = bookingData.matchType;
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-delete-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                () => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else {
              showToast(
                "booking-create-success",
                "Prenotazione salvata correttamente",
                "",
                "success"
              );
              reset();
              handleBookingModalClose();
              setSaveLoading(false);
            }
          },
          () => {
            setSaveLoading(false);
            setLoading(false);
            showToast(
              "booking-delete-error",
              "Errore nella conferma della prenotazione",
              "",
              "danger"
            );
          }
        );
      } else {
        const payload: CreateBookingDTO = {
          date: selectedDate.format("YYYY-MM-DDTHH:mm:ss").split("T")[0],
          time: selectedTime.time || "",
          facilityId: selectedFacility.id!,
          structureId: selectedFacility.structureId,
          userId: loggedUser!.id,
          status: BookingStatus.Pending,
          scope: bookingData.scope || BookingScope.Private,
          bookingType: bookingData.bookingType || BookingType.Friendly,
          inviteLevel: bookingData.inviteLevel,
          coachId: bookingData.coach,
          sportId: selectedFacility.sportId,
          participantIds: [],
          createdByHost: true,
          notes: bookingData.notes || "",
          color: bookingData.color || null,
          customType: bookingData.customType?.trim() || null,
        };
        rememberCustomType(payload.customType);
        setSaveLoading(true);
        Bookings.createBooking(payload).then(
          ({ data }) => {
            const matchPayload: CreateMatchResultDTO = {
              sportId: data.booking.sportId,
              bookingId: data.booking.id,
              matchType: bookingData.matchType || MatchType.Double,
              score: [],
              player1Team1Id: bookingData.player1Team1,
              player2Team1Id: bookingData.player2Team1,
              player1Team2Id: bookingData.player1Team2,
              player2Team2Id: bookingData.player2Team2,
            };
            if (data.booking.sportId === 1) {
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-create-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                (err) => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else if (data.booking.sportId === 2) {
              matchPayload.matchType = bookingData.matchType;
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-create-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                () => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else if (data.booking.sportId === 7) {
              matchPayload.matchType = bookingData.matchType;
              Matches.createMatch(matchPayload).then(
                () => {
                  showToast(
                    "booking-delete-success",
                    "Prenotazione salvata correttamente",
                    "",
                    "success"
                  );
                  reset();
                  handleBookingModalClose();
                  setSaveLoading(false);
                },
                () => {
                  setSaveLoading(false);
                  setLoading(false);
                  showToast(
                    "booking-create-error",
                    "Errore nella conferma della prenotazione",
                    "",
                    "danger"
                  );
                }
              );
            } else {
              showToast(
                "booking-create-success",
                "Prenotazione salvata correttamente",
                "",
                "success"
              );
              reset();
              handleBookingModalClose();
              setSaveLoading(false);
            }
          },
          () => {
            setSaveLoading(false);
            setLoading(false);
            showToast(
              "booking-delete-error",
              "Errore nella conferma della prenotazione",
              "",
              "danger"
            );
          }
        );
      }
    }
  }, [
    selectedDate,
    selectedTime,
    selectedFacility,
    showToast,
    bookingData,
    handleBookingModalClose,
    loggedUser,
    reset,
    selectedSport,
  ]);

  const updateBooking = useCallback(
    (booking: CompleteBooking) => {
      setSaveLoading(true);
      const updatePayload: UpdateBookingDTO = {
        scope: bookingData.scope,
        bookingType: bookingData.bookingType,
        coachId: bookingData.coach,
        inviteLevel:
          bookingData.scope === BookingScope.Private
            ? undefined
            : bookingData.inviteLevel,
        notes: bookingData.notes || "",
        // Solo le prenotazioni della struttura hanno il colore; per le altre
        // si lascia com'è (undefined = campo non inviato).
        color: booking.createdByHost ? bookingData.color || null : undefined,
        customType: booking.createdByHost
          ? bookingData.customType?.trim() || null
          : undefined,
      };
      rememberCustomType(updatePayload.customType);
      Bookings.updateBooking(booking.id, updatePayload).then(
        () => {
          if (booking.match) {
            const matchPayload: UpdateMatchDTO = {
              player1Team1Id: bookingData.player1Team1,
              player2Team1Id: bookingData.player2Team1,
              player1Team2Id: bookingData.player1Team2,
              player2Team2Id: bookingData.player2Team2,
            };
            Matches.updateMatchPlayers(
              booking.sportId,
              booking.match.id,
              matchPayload
            ).then(
              () => {
                showToast(
                  "booking-update-success",
                  "Prenotazione aggiornata correttamente",
                  "",
                  "success"
                );
                reset();
                handleBookingModalClose();
                setSaveLoading(false);
              },
              () => {
                setSaveLoading(false);
                setLoading(false);
                showToast(
                  "booking-update-error",
                  "Errore nell'aggiornamento della prenotazione",
                  "",
                  "danger"
                );
              }
            );
          } else {
            showToast(
              "booking-update-success",
              "Prenotazione aggiornata correttamente",
              "",
              "success"
            );
            reset();
            handleBookingModalClose();
            setSaveLoading(false);
          }
        },
        () => {
          setSaveLoading(false);
          setLoading(false);
          showToast(
            "booking-delete-error",
            "Errore nella conferma della prenotazione",
            "",
            "danger"
          );
        }
      );
    },
    [bookingData, reset, showToast, handleBookingModalClose]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedBooking) {
      handleCreateBooking();
    } else {
      updateBooking(selectedBooking);
    }
  }, [selectedBooking, handleCreateBooking, updateBooking]);

  const handleMarkBookingAsCompleted = useCallback(() => {
    if (selectedBooking) {
      setSaveLoading(true);
      Bookings.updateBooking(selectedBooking.id, {
        status: BookingStatus.Completed,
      }).then(
        () => {
          showToast(
            "booking-update-success",
            "Prenotazione aggiornata correttamente",
            "",
            "success"
          );
          reset();
          handleBookingModalClose();
          setSaveLoading(false);
        },
        () => {
          setSaveLoading(false);
          setLoading(false);
          showToast(
            "booking-create-error",
            "Errore nell'aggiornamento della prenotazione",
            "",
            "danger"
          );
        }
      );
    }
  }, [selectedBooking, handleBookingModalClose, reset, showToast]);

  const disabledSaveButton = useMemo(() => {
    // Tipologia personalizzata scelta ma senza nome.
    if (bookingData.customType !== undefined && !bookingData.customType.trim())
      return true;
    if (![1, 2, 7].includes(selectedSport || 1))
      return bookingData.bookingType === undefined;
    return (
      bookingData.bookingType === BookingType.Training &&
      (!bookingData.coach ||
        (!bookingData.player1Team1 &&
          !bookingData.player2Team1 &&
          !bookingData.player1Team2 &&
          !bookingData.player2Team2))
    );
  }, [bookingData, selectedSport]);

  const handleModalModeToggle = useCallback(() => {
    setModalMode((previous) => (previous === "view" ? "edit" : "view"));
  }, []);

  const handleUpdateResultClick = useCallback(() => {
    if (selectedBooking) {
      setShowBookingModal(false);
      setUpdateResultModal(true);
    }
  }, [selectedBooking]);

  const handleUpdateResultModalClose = useCallback(() => {
    setUpdateResultModal(false);
    setShowBookingModal(true);
  }, []);

  const onUpdateResultSave = useCallback(
    (body: {
      player1Team1Id?: string;
      player2Team1Id?: string;
      player1Team2Id?: string;
      player2Team2Id?: string;
      winnerTeam?: WinnerTeam;
      result?: PadelScore[];
    }) => {
      setSaveLoading(true);
      Matches.updateMatch(
        selectedBooking!.sportId,
        selectedBooking!.match!.id,
        body
      ).then(
        () => {
          showToast(
            "booking-update-success",
            "Prenotazione aggiornata correttamente",
            "",
            "success"
          );
          reset();
          handleBookingModalClose();
          setSaveLoading(false);
        },
        () => {
          setSaveLoading(false);
          setLoading(false);
          showToast(
            "match-result-update-error",
            "Errore nell'aggiornamento del risultato",
            "",
            "danger"
          );
        }
      );
    },
    [selectedBooking, handleBookingModalClose, reset, showToast]
  );

  return (
    <Container fluid className="bk-page">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal show={updateResultModal} onHide={handleUpdateResultModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Aggiorna Risultato</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <UpdateResult
            onSave={onUpdateResultSave}
            loading={loading || saveLoading}
            booking={selectedBooking}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Cancella Prenotazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Sei sicuro di voler cancellare la prenotazione?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={deleteBooking}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      {showBookingModal && (
        <Modal show={showBookingModal} onHide={handleBookingModalClose}>
          <Modal.Header closeButton>
            <Modal.Title>
              {`${selectedBooking ? "" : "Nuova "}Prenotazione${
                selectedBooking
                  ? " del " +
                    new Date(selectedBooking.date).toLocaleDateString("it-IT")
                  : ""
              }`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedBooking ? (
              <BookingModal
                handleFieldChange={handleFieldChange}
                handleFieldClear={handleFieldClear}
                handleInviteLevelChange={handleInviteLevelChange}
                bookingData={bookingData}
                mode={modalMode}
                booking={selectedBooking}
              />
            ) : (
              <NewBookingModal
                facility={selectedFacility}
                date={selectedDate}
                time={selectedTime}
                sport={sports?.find((sport) => sport.value === selectedSport)!}
                handleFieldChange={handleFieldChange}
                handleFieldClear={handleFieldClear}
                handleInviteLevelChange={handleInviteLevelChange}
                bookingData={bookingData}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              disabled={loading || saveLoading}
              variant="light"
              size="sm"
              className="border"
              onClick={handleBookingModalClose}
            >
              {loading || saveLoading ? (
                <TailSpin width={20} height={20} />
              ) : (
                <span>Chiudi</span>
              )}
            </Button>
            {selectedBooking && (
              <>
                {modalMode === "view" && (
                  <Button
                    disabled={loading || saveLoading}
                    variant="primary"
                    size="sm"
                    onClick={handleModalModeToggle}
                  >
                    {loading || saveLoading ? (
                      <TailSpin width={20} height={20} />
                    ) : (
                      <span>
                        <FontAwesomeIcon icon={faEdit} /> Modifica
                      </span>
                    )}
                  </Button>
                )}
                <Button
                  disabled={loading || saveLoading}
                  variant="danger"
                  size="sm"
                  onClick={onDeleteBooking}
                >
                  {loading || saveLoading ? (
                    <TailSpin width={20} height={20} />
                  ) : (
                    <span>
                      <FontAwesomeIcon icon={faTrash} /> Cancella
                    </span>
                  )}
                </Button>
              </>
            )}
            {(!selectedBooking ||
              (selectedBooking && modalMode === "edit")) && (
              <Button
                disabled={disabledSaveButton || loading || saveLoading}
                onClick={handleSubmit}
                variant="success"
                size="sm"
              >
                {saveLoading ? (
                  <TailSpin width={20} height={20} />
                ) : (
                  <span>
                    <FontAwesomeIcon icon={faSave} /> Salva
                  </span>
                )}
              </Button>
            )}
            {selectedBooking &&
              selectedBooking.status !== BookingStatus.Completed &&
              isPastDateTime(
                selectedBooking.date,
                selectedBooking.time.split("-")[0]
              ) && (
                <Button
                  disabled={loading || saveLoading}
                  onClick={handleMarkBookingAsCompleted}
                  variant="success"
                  size="sm"
                >
                  {saveLoading ? (
                    <TailSpin width={20} height={20} />
                  ) : (
                    <span>
                      <FontAwesomeIcon icon={faCheck} /> Completa
                    </span>
                  )}
                </Button>
              )}
            {selectedBooking &&
              [1, 2, 7].includes(selectedBooking.sportId) &&
              !selectedBooking.customType &&
              selectedBooking.bookingType !== BookingType.Training &&
              selectedBooking.status === BookingStatus.Completed && (
                <Button
                  disabled={loading || saveLoading}
                  onClick={handleUpdateResultClick}
                  variant="success"
                  size="sm"
                >
                  {saveLoading ? (
                    <TailSpin width={20} height={20} />
                  ) : (
                    <span>
                      <FontAwesomeIcon icon={faList} /> Aggiorna risultato
                    </span>
                  )}
                </Button>
              )}
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}
